import api from "../api/client";

/**
 * Download a file from an AUTHENTICATED endpoint.
 *
 * WHY THIS EXISTS. The obvious thing - `<a href="/api/v1/.../file.pdf">` - does
 * not work in this app and fails in a confusing way. A link is a NAVIGATION,
 * and a navigation carries no Authorization header; AeroGauge authenticates
 * with a bearer token from localStorage rather than a session cookie. So the
 * browser leaves the app, the API answers 401, and the user is looking at
 *
 *     {"detail": "Not authenticated"}
 *
 * where a file should have been. Reported on the Teams app package; the same
 * mistake was also live on the integrations reference PDF and the PSA quote
 * PDF, because it looks right and nothing catches it.
 *
 * Fetch through the API client, which attaches the token, then hand the bytes
 * to the browser as a blob.
 */
export async function downloadAuthed(
  path: string,
  filename: string,
  mime = "application/octet-stream",
): Promise<void> {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The server's error message, dug out of a failed blob request.
 *
 * With `responseType: "blob"` an error body arrives as a Blob too, so the
 * usual `e.response.data.detail` is undefined and the caller shows a generic
 * message over a perfectly good explanation.
 */
export async function blobErrorDetail(e: any, fallback: string): Promise<string> {
  try {
    const text = await e?.response?.data?.text?.();
    if (text) return JSON.parse(text).detail || fallback;
  } catch {
    /* fall through */
  }
  return e?.response?.data?.detail || fallback;
}
